/* eslint-disable react/function-component-definition */
import { useMutation, useQuery } from "@apollo/client";
import AssetDiscussion from "components/discussions/AssetDiscussion";
import Financials from "components/financials/modern/Financials";
import RootIssuerProxy from "components/financials/modern/RootIssuerProxy";
import { COMPANY_ISSUER_NAVIGATION_HISTORY_ATOM } from "components/financials/modern/atoms";
import { useSelectedCompanyIssuer } from "components/financials/modern/data/useCompanyIssuers";
import { useFinancialsQueryState } from "components/financials/modern/data/useFinancialsState";
import useIndicatorsByObjectId from "components/financials/modern/data/useIndicatorsByObjectId";
import { useSpecificNode } from "components/financials/modern/data/useNodes";
import type { IFinancialsTemplateFieldsFragment } from "components/financials/modern/graphql/fragments/__generated__/FinancialsTemplateFieldsFragment.generated";
import type { IIndicatorFieldsFragment } from "components/financials/modern/graphql/fragments/__generated__/IndicatorFieldsFragment.generated";
import { getAssetType, platformVerticalToSecurityCountry } from "components/financials/modern/utils/platformVertical";
import type { SelectedCompanyIssuer } from "components/issuers/IssuerByTickerSymbolSearch";
import IssuerByTickerSymbolSearch, {
  IssuerByTickerSymbolSearchButtonTrigger,
  IssuerByTickerSymbolSearchInputTrigger,
} from "components/issuers/IssuerByTickerSymbolSearch";
import { IssuerNavigationHistoryStack } from "components/issuers/IssuerNavigationHistoryStack";
import ModernIssuerHeader from "components/issuers/ModernIssuerHeader";
import { issuerRefToGraphQLInput } from "components/issuers/utils";
import NavbarLayout from "components/layouts/NavbarLayout";
import Masthead from "components/pages/FinancialsPage/Masthead";
import LoadingSkeleton from "components/pages/TemplatesGalleryPage/LoadingSkeleton";
import FundamenteiMetadata from "components/seo/FundamenteiMetadata";
import useChangeSetting from "components/settings/hooks/useChangeSetting";
import TwoPassRender from "components/shared/TwoPassRender";
import ContentContainerBox from "components/stocks/ContentContainerBox";
import { Separator } from "components/ui/Separator";
import classNames from "components/ui/classNames";
import emptyFunction from "components/utils/emptyFunction";
import { companyIssuerRefSchema } from "components/ux/urlParsers";
import getUnixTime from "date-fns/getUnixTime";
import { useAtomValue, useSetAtom } from "jotai";
import { useHydrateAtoms } from "jotai/utils";
import type { ICompanyIssuerFieldsFragment } from "lib/graphql/fragments/__generated__/CompanyIssuerFields.generated";
import ClearSelectedCompaniesHistoryMutation from "lib/graphql/mutations/ClearSelectedCompaniesHistoryMutation";
import SelectedCompaniesHistoryQuery from "lib/graphql/queries/SelectedCompaniesHistoryQuery";
import type { ISelectedCompaniesHistoryQuery } from "lib/graphql/queries/__generated__/SelectedCompaniesHistoryQuery.generated";
import useSelfie from "lib/hooks/useSelfie";
import getBaseUrl from "lib/utils/getBaseUrl";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import uniqBy from "lodash/uniqBy";
import type { NextPage } from "next";
import { useQueryState } from "nuqs";
import { startTransition, useState } from "react";
import { toast } from "react-hot-toast";
import type { IntlShape } from "react-intl";
import { useIntl } from "react-intl";
import {
  IFinancialsStatementType,
  IIndicatorDataSource,
  IPlatformVertical,
  ISecurityCountry,
} from "types/graphqlTypes";

interface FinancialsPageProps {}

const FinancialsPage: NextPage<FinancialsPageProps> = () => {
  const intl = useIntl();
  const selfie = useSelfie();

  const [issuerRef, setIssuerRef] = useQueryState("issuer", companyIssuerRefSchema);
  const { issuer, loading: isLoadingIssuer } = useSelectedCompanyIssuer(
    issuerRef ? issuerRefToGraphQLInput(issuerRef) : null,
  );
  const [adversaries, setAdversaries] = useState<SelectedCompanyIssuer[] | null>(null);
  const [arbitraryIndicators, setArbitraryIndicators] = useState<IIndicatorFieldsFragment[]>([]);
  const setSelectedIssuersHistory = useSetAtom(COMPANY_ISSUER_NAVIGATION_HISTORY_ATOM);
  const { data: selectedCompaniesHistoryQuery } = useQuery<ISelectedCompaniesHistoryQuery>(
    SelectedCompaniesHistoryQuery,
    {
      onCompleted: ({ selectedCompaniesHistory }) => {
        const history = (selectedCompaniesHistory || []).map((issuer) => ({
          company: issuer,
          financialsTemplateId: null,
          timeStamp: getUnixTime(new Date()),
        }));
        setSelectedIssuersHistory((current) => {
          if (current.length === 0) {
            return history;
          }
          return current;
        });
      },
    },
  );
  const previousNavigationHistory = (selectedCompaniesHistoryQuery?.selectedCompaniesHistory || []).map((issuer) => {
    return {
      company: issuer,
      financialsTemplateId: null,
      timeStamp: getUnixTime(new Date()),
    };
  });
  useHydrateAtoms([
    [
      COMPANY_ISSUER_NAVIGATION_HISTORY_ATOM,
      issuer
        ? uniqBy(
            [
              {
                company: issuer,
                financialsTemplateId: null,
                // O motivo do `getUnixTime` é que fica consistente, sempre em UTC. Se usarmos `new Date()`, ele vai pegar
                // a data local do usuário, ao invés de manter uma consistência com a data do server.
                timeStamp: getUnixTime(new Date()),
              },
              ...previousNavigationHistory,
            ],
            ({ company }) => company._id,
          )
        : previousNavigationHistory,
    ],
  ]);
  const selectedIssuersHistory = useAtomValue(COMPANY_ISSUER_NAVIGATION_HISTORY_ATOM);
  const [clearSelectedCompaniesHistory] = useMutation(ClearSelectedCompaniesHistoryMutation, {
    onCompleted: () => {
      toast.success(
        intl.formatMessage({
          id: "Oa8b4R",
          defaultMessage: "Histórico de navegação limpo!",
        }),
        {
          position: "top-center",
          duration: 2200,
        },
      );
    },
  });
  const {
    tab,
    interim,
    statementType,
    dataSource,
    currency,
    reportDates,
    templateId,
    selectedIndicators,
    limit,

    setStatementTypeWithInterim,
    setDataSource,
    setCurrency,
    setReportDates,
    setTemplateId,
    setTab,
    setSelectedIndicators,
    setLimit,
  } = useFinancialsQueryState();
  const { indicators } = useIndicatorsByObjectId(selectedIndicators.map(({ objectId }) => objectId));
  const { node: financialsTemplate } = useSpecificNode(templateId, "FinancialsTemplate");

  const { value: currentTableSettings } = useChangeSetting("financialsTable", {
    sortDirection: "ASC",
    textAlignment: "CENTER",
    fullBleed: false,
  });

  const gitShaShort = process.env.GIT_SHA_SHORT || "1";
  const imageUrlNamespace = issuer ? platformVerticalToSecurityCountry(issuer.vertical).toLowerCase() : null;
  const languageCode = intl.locale === "pt-BR" ? "pt" : "en";
  const imageUrl = issuer
    ? `${getBaseUrl()}/og/issuer/${issuer.tickerSymbol.toLowerCase()}?locale=${languageCode}&ns=${imageUrlNamespace}&v=${gitShaShort}&locale=${intl.locale}`
    : null;
  const pageDescription = intl.formatMessage({
    id: "+I2FEE",
    defaultMessage:
      "Balanços atualizados de mais de 6000 ativos, com mais de 900 indicadores disponíveis para análise customizada.",
  });

  const onChangeTemplate = (template: IFinancialsTemplateFieldsFragment | null) => {
    setTemplateId(template?.objectId || null);
    startTransition(() => {
      setSelectedIssuersHistory((currentHistory) => {
        return currentHistory.map((node) => {
          if (issuer && node.company._id === issuer._id) {
            return {
              ...node,
              financialsTemplateId: template?.objectId || null,
            };
          }
          return node;
        });
      });
    });
  };

  return (
    <NavbarLayout>
      <FundamenteiMetadata
        title={getIntlPageTitle(intl, tab, issuer)}
        description={(issuer && issuer.description) || pageDescription}
        openGraph={{
          images:
            imageUrl && issuer
              ? [
                  {
                    url: imageUrl,
                    width: 1200,
                    height: 630,
                    alt: `${issuer.tickerSymbol} - ${issuer.name}`,
                  },
                ]
              : [],
        }}
      />

      {!issuer && <Masthead vertical={null} />}

      <div className="xl:px-4">
        <div className={classNames("mx-auto max-w-7xl space-y-6 px-6 py-8 lg:px-8 xl:px-0")}>
          {issuer && <ModernIssuerHeader key={issuer._id} issuer={issuer} loading={isLoadingIssuer} />}

          {(selectedIssuersHistory.length > 0 || issuer) && (
            <IssuerByTickerSymbolSearch
              disabled={!issuerRef || tab === "COMPARE"}
              onChange={(issuer) => {
                setIssuerRef(issuer);
                setDataSource(null);
                setTab("TABLE");
                setReportDates({
                  before: null,
                  after: null,
                });
                setTemplateId(null);
                const isInHistory = selectedIssuersHistory.some((node) => node.company._id === issuer._id);
                setSelectedIssuersHistory(
                  isInHistory
                    ? selectedIssuersHistory
                    : [
                        { company: issuer, financialsTemplateId: null, timeStamp: getUnixTime(new Date()) },
                        ...selectedIssuersHistory,
                      ],
                );
              }}
            >
              <div>
                <IssuerNavigationHistoryStack
                  history={selectedIssuersHistory.slice(0, 20).filter(notUndefinedOrNull)}
                  onClearHistory={() => {
                    if (issuer) {
                      setSelectedIssuersHistory([
                        {
                          company: issuer,
                          financialsTemplateId: null,
                          timeStamp: getUnixTime(new Date()),
                        },
                      ]);
                      clearSelectedCompaniesHistory();
                    } else {
                      setSelectedIssuersHistory([]);
                      clearSelectedCompaniesHistory();
                    }
                  }}
                  onIssuerClick={({ company: issuer, financialsTemplateId }) => {
                    setIssuerRef(issuer);
                    setTab("TABLE");
                    setReportDates({
                      before: null,
                      after: null,
                    });
                    setTemplateId(financialsTemplateId || null);
                    if (issuer.vertical !== IPlatformVertical.Acoes) {
                      setDataSource(null);
                    }
                  }}
                >
                  {issuer && tab !== "COMPARE" && <IssuerByTickerSymbolSearchButtonTrigger variant="translucid" />}
                </IssuerNavigationHistoryStack>
              </div>
            </IssuerByTickerSymbolSearch>
          )}

          {!issuer && (
            <IssuerByTickerSymbolSearch
              onChange={(issuer) => {
                setIssuerRef(issuer);
                setDataSource(null);
                setReportDates({
                  before: null,
                  after: null,
                });
                setSelectedIssuersHistory(
                  [
                    issuer ? { company: issuer, timeStamp: getUnixTime(new Date()) } : null,
                    ...selectedIssuersHistory,
                  ].filter(notUndefinedOrNull),
                );
              }}
            >
              <IssuerByTickerSymbolSearchInputTrigger
                size="big"
                placeholder={intl.formatMessage({
                  defaultMessage: "Pesquise um ticker...",
                  id: "0QgUCr",
                })}
              />
            </IssuerByTickerSymbolSearch>
          )}

          {!issuer && isLoadingIssuer && <LoadingSkeleton count={1} className="h-full min-h-96" />}
        </div>

        {issuer && (
          <RootIssuerProxy issuer={issuer}>
            {({ loading, issuer: rootIssuer }) => {
              if (loading) {
                return (
                  <div className={classNames("mx-auto max-w-screen-2xl space-y-8 px-6 pb-12 lg:px-8 xl:px-0")}>
                    <LoadingSkeleton count={1} className="h-full min-h-96" />
                  </div>
                );
              }
              return (
                <>
                  <div
                    className={classNames("mx-auto max-w-screen-2xl space-y-6 px-4 pb-12 lg:px-8 xl:px-0", {
                      "max-w-full lg:px-8 xl:px-8": currentTableSettings?.fullBleed,
                    })}
                  >
                    {(issuer.vertical === IPlatformVertical.Stocks || issuer.vertical === IPlatformVertical.Reits) && (
                      <TwoPassRender>
                        {() => (
                          <div
                            className={classNames(
                              "px-4 sm:px-6",
                              "relative rounded-xl border bg-white px-4 py-6 sm:px-6",
                              "ring-4 ring-slate-100 ring-offset-1 ring-offset-slate-100",
                              "dark:ring-1 dark:ring-slate-950 dark:ring-offset-2 dark:ring-offset-slate-900",
                              "dark:border-transparent dark:bg-slate-900",
                              "p-0 dark:border-none sm:px-0",
                            )}
                          >
                            <ContentContainerBox
                              className={classNames(
                                "px-4 sm:px-6",
                                "relative rounded-xl border bg-white px-4 py-6 sm:px-6",
                                "ring-4 ring-slate-100 ring-offset-1 ring-offset-slate-100",
                                "dark:ring-1 dark:ring-slate-950 dark:ring-offset-2 dark:ring-offset-slate-900",
                                "dark:border-transparent dark:bg-slate-900",
                                "p-0 dark:border-none sm:px-0",
                              )}
                            >
                              <Financials
                                tab={tab}
                                indicatorDataSource={IIndicatorDataSource.Morningstar}
                                country={ISecurityCountry.Us}
                                currency={currency || "USD"}
                                tickerSymbol={issuer.tickerSymbol}
                                statementType={statementType || IFinancialsStatementType.Annual}
                                interim={interim}
                                reportDates={reportDates}
                                templateId={templateId}
                                indicators={indicators}
                                adversaries={adversaries || []}
                                limit={limit}
                                onChangeLimit={(limit) => {
                                  setLimit(limit === 10 ? null : (limit as 10 | 15 | 30 | 45));
                                }}
                                onChangeTab={(tab) => {
                                  setTab(tab);
                                  setStatementTypeWithInterim({
                                    statementType: null,
                                    interim: null,
                                  });
                                  setSelectedIndicators(null);
                                  setAdversaries(null);
                                }}
                                onChangeIndicators={(indicators) => {
                                  const nextIndicators = uniqBy(indicators.slice(0, 5), "objectId");
                                  setSelectedIndicators(nextIndicators.length === 0 ? null : nextIndicators);
                                }}
                                onChangeStatementType={(statementType) => {
                                  setReportDates({
                                    before: null,
                                    after: null,
                                  });
                                  setStatementTypeWithInterim({
                                    statementType: statementType.toLowerCase() as IFinancialsStatementType,
                                    interim: null,
                                  });
                                }}
                                onChangeInterim={(interim) => {
                                  setStatementTypeWithInterim({
                                    interim: interim || null,
                                  });
                                }}
                                onChangeCurrency={(currency) => setCurrency(currency)}
                                onChangeReportDates={([after, before]) => {
                                  setReportDates({
                                    before,
                                    after,
                                  });
                                }}
                                onChangeTemplate={onChangeTemplate}
                                onChangeAdversaries={(adversaries) => {
                                  setAdversaries(adversaries.length === 0 ? null : adversaries);
                                }}
                                onChangeIndicatorDataSource={emptyFunction}
                              />
                            </ContentContainerBox>
                          </div>
                        )}
                      </TwoPassRender>
                    )}
                    {issuer.vertical === IPlatformVertical.Acoes && (
                      <TwoPassRender>
                        {() => (
                          <ContentContainerBox
                            className={classNames(
                              "px-4 sm:px-6",
                              "relative rounded-xl border bg-white px-4 py-6 sm:px-6",
                              "ring-4 ring-slate-100 ring-offset-1 ring-offset-slate-100",
                              "dark:ring-1 dark:ring-slate-950 dark:ring-offset-2 dark:ring-offset-slate-900",
                              "dark:border-transparent dark:bg-slate-900",
                              "p-0 dark:border-none sm:px-0",
                            )}
                          >
                            <Financials
                              tab={tab}
                              indicatorDataSource={dataSource || IIndicatorDataSource.Cvm}
                              country={ISecurityCountry.Br}
                              currency={currency || "BRL"}
                              tickerSymbol={issuer.tickerSymbol}
                              reportDates={reportDates}
                              statementType={statementType || IFinancialsStatementType.Annual}
                              interim={interim}
                              canChangeDataSource={true}
                              limit={limit}
                              adversaries={adversaries || []}
                              indicators={arbitraryIndicators}
                              templateId={financialsTemplate?.objectId || templateId}
                              onChangeIndicators={(indicators) => {
                                setArbitraryIndicators(indicators);
                              }}
                              onChangeLimit={(limit) => {
                                setLimit(limit === 10 ? null : (limit as 10 | 15 | 30 | 45));
                              }}
                              onChangeTab={(tab) => {
                                setTab(tab);
                                setStatementTypeWithInterim({
                                  statementType: null,
                                  interim: null,
                                });
                                setSelectedIndicators(null);
                                setAdversaries(null);
                              }}
                              onChangeStatementType={(statementType) => {
                                setReportDates({
                                  before: null,
                                  after: null,
                                });
                                setStatementTypeWithInterim({
                                  statementType: statementType.toLowerCase() as IFinancialsStatementType,
                                  interim: null,
                                });
                              }}
                              onChangeIndicatorDataSource={(dataSource) => {
                                setDataSource(dataSource);
                                setTemplateId(null);
                                setReportDates({
                                  before: null,
                                  after: null,
                                });
                              }}
                              onChangeInterim={(interim) => {
                                setStatementTypeWithInterim({
                                  interim: interim || null,
                                });
                              }}
                              onChangeCurrency={(currency) => setCurrency(currency)}
                              onChangeReportDates={([after, before]) => {
                                setReportDates({
                                  before,
                                  after,
                                });
                              }}
                              onChangeTemplate={onChangeTemplate}
                              onChangeAdversaries={(adversaries) => {
                                setAdversaries(adversaries.length === 0 ? null : adversaries);
                              }}
                            />
                          </ContentContainerBox>
                        )}
                      </TwoPassRender>
                    )}
                    {issuer.vertical === IPlatformVertical.Fiis && (
                      <TwoPassRender>
                        {() => (
                          <ContentContainerBox
                            className={classNames(
                              "px-4 sm:px-6",
                              "relative rounded-xl border bg-white px-4 py-6 sm:px-6",
                              "ring-4 ring-slate-100 ring-offset-1 ring-offset-slate-100",
                              "dark:ring-1 dark:ring-slate-950 dark:ring-offset-2 dark:ring-offset-slate-900",
                              "dark:border-transparent dark:bg-slate-900",
                              "p-0 dark:border-none sm:px-0",
                            )}
                          >
                            <Financials
                              tab={tab}
                              indicatorDataSource={IIndicatorDataSource.Fnet}
                              country={ISecurityCountry.Br}
                              tickerSymbol={issuer.tickerSymbol}
                              currency={currency || "BRL"}
                              reportDates={reportDates}
                              adversaries={adversaries || []}
                              limit={limit}
                              onChangeCurrency={(currency) => setCurrency(currency)}
                              onChangeLimit={(limit) => {
                                setLimit(limit === 10 ? null : (limit as 10 | 15 | 30 | 45));
                              }}
                              onChangeTab={(tab) => {
                                setTab(tab);
                                setStatementTypeWithInterim({
                                  statementType: null,
                                  interim: null,
                                });
                                setSelectedIndicators(null);
                                setAdversaries(null);
                              }}
                              onChangeReportDates={([after, before]) => {
                                setReportDates({
                                  before,
                                  after,
                                });
                              }}
                              onChangeAdversaries={(adversaries) => {
                                setAdversaries(adversaries.length === 0 ? null : adversaries);
                              }}
                              onChangeIndicatorDataSource={emptyFunction}
                              onChangeStatementType={emptyFunction}
                              onChangeInterim={emptyFunction}
                              onChangeTemplate={onChangeTemplate}
                              onChangeIndicators={emptyFunction}
                            />
                          </ContentContainerBox>
                        )}
                      </TwoPassRender>
                    )}
                  </div>

                  {rootIssuer && rootIssuer.__typename !== "BrazilianRealEstateFundIssuer" && selfie && (
                    <div className={classNames("mx-auto max-w-screen-lg space-y-12 px-6 pb-12 lg:px-8 xl:px-0")}>
                      <Separator />

                      <div
                        className={classNames(
                          "relative w-full rounded-xl bg-white shadow",
                          "border border-slate-200 ring-4 ring-slate-100 ring-offset-1 ring-offset-slate-100",
                          "dark:ring-1 dark:ring-slate-950 dark:ring-offset-2 dark:ring-offset-slate-900",
                          "dark:border-transparent dark:bg-slate-900",
                        )}
                      >
                        <AssetDiscussion
                          assetType={getAssetType(rootIssuer)}
                          assetId={issuer._id}
                          hideHeaderLine={true}
                        />
                      </div>
                    </div>
                  )}
                </>
              );
            }}
          </RootIssuerProxy>
        )}
      </div>
    </NavbarLayout>
  );
};

function getIntlPageTitle(
  intl: IntlShape,
  tab: "COMPARE" | "CHART" | "TABLE",
  issuer: Omit<ICompanyIssuerFieldsFragment, "__typename"> | null,
) {
  if (!issuer) {
    return intl.formatMessage({
      id: "Pq19tm",
      defaultMessage: "Balanços | Fundamentei",
    });
  }
  if (tab === "TABLE") {
    return intl.formatMessage(
      {
        id: "TwO8SN",
        defaultMessage: "{tickerSymbol} - {name} | Balanços | Fundamentei",
      },
      {
        tickerSymbol: issuer.tickerSymbol,
        name: issuer.name,
      },
    );
  }
  if (tab === "CHART") {
    return intl.formatMessage(
      {
        id: "Sml5Re",
        defaultMessage: "{tickerSymbol} - {name} | Gráfico Fundamentalista | Fundamentei",
      },
      {
        tickerSymbol: issuer.tickerSymbol,
        name: issuer.name,
      },
    );
  }
  return intl.formatMessage(
    {
      id: "dvFYZO",
      defaultMessage: "{tickerSymbol} - {name} | Comparativo | Fundamentei",
    },
    {
      tickerSymbol: issuer.tickerSymbol,
      name: issuer.name,
    },
  );
}

export default FinancialsPage;
